import { Col, Container, Dropdown, Row } from 'react-bootstrap';
import { MegaMenuSection, Route, RouteItems } from 'sitemap';
import { capitalize } from 'helpers/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import classNames from 'classnames';
import { useState } from 'react';
import MegaMenuRecentProjects from './MegaMenuRecentProjects';
import { getCookie } from 'app/shared/utils/cookie.utils';

export const MEGA_MENU_PRODUCTS = [
  {
    id: 'TSC+',
    title: 'TSC+ Trust, Safety & Compliance',
    description: 'Govern AI behavior through ethics, fairness, explainability, and regulatory alignment.',
    icon: 'shield',
    iconColor: '#2563eb'
  },
  {
    id: 'CSA+',
    title: 'CSA+ Cyber Security & AI Compliance',
    description: 'Secure AI behavior across infrastructure, logs, and operational signals.',
    icon: 'lock',
    iconColor: '#f59e0b'
  },
  {
    id: 'MTC+',
    title: 'MTC+ Multimodal Trust & Compliance',
    description: 'Govern AI-generated and AI-impacted media using compliance and risk controls.',
    icon: 'image',
    iconColor: '#7c3aed'
  },
  {
    id: 'ATE+',
    title: 'ATE+ AI Trust Evaluation',
    description:
      'Assess and certify AI models, applications, and agents for trustworthiness, reliability, safety, and production readiness.',
    icon: 'award',
    iconColor: '#059669'
  }
];

interface TopNavMegaMenuProps {
  route: RouteItems;
  onCloseMenu?: () => void;
  show?: boolean;
  onProductSelect?: (productId: string) => void;
}

const TopNavMegaMenu = ({ route, onCloseMenu, show = false, onProductSelect }: TopNavMegaMenuProps) => {
  const sections = route.megaMenuSections ?? buildSectionsFromPages(route.pages);

  // State for the dashboard layout to track hovered item
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(1); // Default to second item (Structured Content)
  // State for modern layout: left tabs, center submenu, right recent projects
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Modern layout: left tabs / center submenu (gradient) / right recent projects
  if (route.megaMenuLayout === 'modern') {
    const menuSections = route.megaMenuSections ?? [];
    const secondRowSections = route.megaMenuSecondRowSections ?? [];
    const allTabs = [...menuSections, ...secondRowSections];
    const activeSection = allTabs[activeTabIndex] ?? allTabs[0];

    return (
      <Dropdown.Menu
        as="ul"
        className="dropdown-menu-card dropdown-mega-menu dropdown-mega-menu-modern py-0 border-0 shadow-sm w-100 rounded-0"
        style={{ marginTop: '0.1rem' }}
      >
        <div
          key={show ? 'visible' : 'hidden'}
          className="mega-menu-panel mega-menu-panel-modern mega-menu-animate-in p-0 rounded-0 d-flex"
          style={{
            minHeight: '320px',
            animation: show ? 'mega-menu-fade-in 0.35s ease-out both' : 'none'
          }}
        >
          {/* Column 1: Left tabs */}
          <div
            className="mega-menu-modern-tabs col-3 d-flex flex-column p-3"
            style={{ background: '#fafafa', borderRight: '1px solid #aae0f5' }}
          >
            <h4
              className="text-uppercase fw-bold mb-3"
              style={{ color: '#6b7280', letterSpacing: '0.05em', fontSize: '1.0625rem' }}
            >
              Features
            </h4>
            <nav role="tablist" className="d-flex flex-column gap-1">
              {allTabs.map((section, idx) => {
                const isActive = idx === activeTabIndex;
                return (
                  <button
                    key={idx}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    onMouseEnter={() => setActiveTabIndex(idx)}
                    onFocus={() => setActiveTabIndex(idx)}
                    onKeyDown={e => {
                      if (e.key === 'ArrowDown' && idx < allTabs.length - 1) {
                        e.preventDefault();
                        setActiveTabIndex(idx + 1);
                      } else if (e.key === 'ArrowUp' && idx > 0) {
                        e.preventDefault();
                        setActiveTabIndex(idx - 1);
                      }
                    }}
                    className={classNames(
                      'd-flex align-items-start gap-3 p-3 rounded-3 text-start border-0 w-100',
                      'text-decoration-none',
                      isActive ? 'bg-body-highlight' : 'bg-transparent'
                    )}
                    style={{
                      outline: 'none',
                      ...(isActive && { backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' })
                    }}
                  >
                    <span
                      className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-2"
                      style={{
                        width: 40,
                        height: 40,
                        backgroundColor: isActive ? '#2563eb' : '#e5e7eb',
                        color: isActive ? '#fff' : '#6b7280'
                      }}
                    >
                      <FeatherIcon icon={(section.icon as any) || 'folder'} size={20} strokeWidth={2} />
                    </span>
                    <div className="min-w-0 flex-grow-1">
                      <div className="fw-bold text-body-emphasis" style={{ fontSize: '1.125rem' }}>
                        {section.sectionTitle}
                      </div>
                      {section.items[0]?.description && (
                        <div
                          className="text-body-secondary mt-1 text-truncate"
                          style={
                            {
                              fontSize: '1rem',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            } as React.CSSProperties
                          }
                        >
                          {section.items[0].description}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
          {/* Column 2: Center submenu with gradient */}
          <div
            className="mega-menu-modern-submenu col-5 d-flex flex-column p-4 overflow-auto"
            style={{
              background: 'linear-gradient(to bottom right, #eef4ff, #f8fbff, white)',
              borderLeft: '1px solid #aae0f5',
              borderRight: '1px solid #aae0f5'
            }}
          >
            <h4
              className="text-uppercase fw-bold mb-3"
              style={{ color: '#6b7280', letterSpacing: '0.05em', fontSize: '1.0625rem' }}
            >
              {getCookie('activeProduct') ?? 'Selected Product'}
            </h4>
            <div className="d-flex flex-column gap-2">
              {activeSection?.items.map((item: Route, itemIdx: number) => (
                <Link
                  key={itemIdx}
                  to={item.path || '#'}
                  onClick={() => onCloseMenu?.()}
                  className="d-flex align-items-start gap-3 p-3 rounded-3 text-decoration-none text-body"
                  style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.8)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <FeatherIcon
                    icon={(item.icon as any) || 'circle'}
                    size={18}
                    className="text-body-secondary flex-shrink-0 mt-1"
                  />
                  <div>
                    <div className="fw-semibold" style={{ fontSize: '1.125rem' }}>
                      {capitalize(item.name)}
                    </div>
                    {item.description && (
                      <div className="text-body-secondary" style={{ fontSize: '1rem' }}>
                        {item.description}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          {/* Column 3: Products */}
          <div
            className="mega-menu-modern-products col-4 d-flex flex-column p-4 overflow-auto"
            style={{ background: '#ffffff', borderLeft: '1px solid #aae0f5' }}
          >
            <h4
              className="text-uppercase fw-bold mb-3"
              style={{ color: '#6b7280', letterSpacing: '0.05em', fontSize: '1.0625rem' }}
            >
              Products
            </h4>
            <div className="d-flex flex-column gap-2">
              {MEGA_MENU_PRODUCTS.map(product => (
                <div
                  key={product.id}
                  role="button"
                  tabIndex={0}
                  className="d-flex align-items-start gap-3 p-3 rounded-3 text-body cursor-pointer"
                  style={{ transition: 'background 0.15s' }}
                  onClick={() => {
                    onProductSelect?.(product.id);
                    onCloseMenu?.();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      onProductSelect?.(product.id);
                      onCloseMenu?.();
                    }
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <FeatherIcon
                    icon={(product.icon as any) || 'box'}
                    size={20}
                    className="flex-shrink-0 mt-1"
                    style={{ color: product.iconColor || '#6b7280' }}
                  />
                  <div>
                    <div className="fw-bold text-black" style={{ fontSize: '1.125rem' }}>
                      {product.title}
                    </div>
                    <div className="text-body-secondary" style={{ fontSize: '0.875rem' }}>
                      {product.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Dropdown.Menu>
    );
  }

  // Apps layout: col-8 = 3 menus horizontally + optional second row (Divisions); col-4 = recently created projects (top-aligned)
  if (route.megaMenuLayout === 'apps') {
    const menuSections = route.megaMenuSections ?? []; // 3 menus only
    const secondRowSections = route.megaMenuSecondRowSections ?? [];

    return (
      <Dropdown.Menu
        as="ul"
        className="dropdown-menu-card dropdown-mega-menu py-0 border-0 shadow-sm w-100 rounded-0"
        style={{ marginTop: '0.1rem' }}
      >
        <div
          className="mega-menu-panel mega-menu-panel-apps ps-5 pe-4 pb-4 pt-5 rounded-0 d-flex flex-column align-items-center"
          style={{ background: '#ffffff' }}
        >
          <Container className="mx-auto w-100">
            <Row className="gx-4 gy-4 mb-0 justify-content-center">
              {/* col-8: 3 menu sections horizontally, then second row (Divisions) */}
              <Col xs={12} md={8} className="pe-4">
                <Row className="gx-4 mega-menu-row flex-nowrap">
                  {menuSections.map((section, sectionIndex) => (
                    <Col key={sectionIndex} className={classNames('mega-menu-col', section.containerClass)}>
                      <h4
                        className={classNames(
                          'mega-menu-section-title text-uppercase fw-bold mb-3 mega-menu-section-title-align',
                          section.titleClass
                        )}
                      >
                        {section.sectionTitle}
                      </h4>
                      <div className="mega-menu-section-items">
                        {section.items.map((item, itemIndex) => (
                          <MegaMenuItem key={itemIndex} item={item} onCloseMenu={onCloseMenu} />
                        ))}
                        {section.allLink && (
                          <Link
                            to={section.allLink.path}
                            className="mega-menu-all-link d-inline-block mt-2 text-primary fw-semibold text-decoration-none"
                          >
                            {section.allLink.label} →
                          </Link>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
                {secondRowSections.length > 0 && (
                  <Row className="gx-4 mt-3 pt-3 border-top">
                    {secondRowSections.map((section, sectionIndex) => (
                      <Col key={sectionIndex}>
                        <h4
                          className={classNames(
                            'mega-menu-section-title text-uppercase fw-bold mb-3 mega-menu-section-title-align',
                            section.titleClass
                          )}
                        >
                          {section.sectionTitle}
                        </h4>
                        <div className="mega-menu-section-items">
                          {section.items.map((item, itemIndex) => (
                            <MegaMenuItem key={itemIndex} item={item} onCloseMenu={onCloseMenu} />
                          ))}
                          {section.allLink && (
                            <Link
                              to={section.allLink.path}
                              className="mega-menu-all-link d-inline-block mt-2 text-primary fw-semibold text-decoration-none"
                            >
                              {section.allLink.label} →
                            </Link>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </Col>
              {/* col-4: recently created projects, aligned to top */}
              <Col xs={12} md={4} className="mega-menu-promo-col align-self-start">
                <MegaMenuRecentProjects />
              </Col>
            </Row>
          </Container>
        </div>
        {route.megaMenuBottomBar && (
          <div className="mega-menu-bottom-bar bg-body-tertiary p-3 rounded-bottom-3 border-top">
            <div className="d-flex align-items-center justify-content-center gap-2">
              {route.megaMenuBottomBar.icon && (
                <FeatherIcon icon={route.megaMenuBottomBar.icon} size={16} className="text-primary" />
              )}
              <span className="fw-semibold text-body-secondary">{route.megaMenuBottomBar.label}</span>
              <Link to={route.megaMenuBottomBar.linkPath} className="fw-bold text-decoration-none">
                {route.megaMenuBottomBar.linkText}
              </Link>
            </div>
          </div>
        )}
      </Dropdown.Menu>
    );
  }

  if (route.megaMenuLayout === 'dashboard' && sections.length >= 2) {
    const rolesSection = sections[0];
    const featuresSection = sections[1];
    const activeFeature = featuresSection.items[activeFeatureIndex] || featuresSection.items[0];

    return (
      <Dropdown.Menu
        as="ul"
        className="dropdown-menu-card dropdown-mega-menu py-0 border-0 shadow-sm w-100 rounded-0"
        style={{ marginTop: '0.1rem' }}
      >
        <div className="mega-menu-panel p-0 rounded-0">
          <Row className="gx-0 mega-menu-row flex-nowrap">
            {/* Column 1: Roles (Left) */}
            <Col className="mega-menu-col col-3 bg-light-subtle p-4 border-end">
              <div className="d-flex flex-column gap-3">
                {rolesSection.items.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path || '#'}
                    className="d-flex align-items-center justify-content-between p-3 rounded-3 text-decoration-none bg-body hover-bg-primary-subtle transition-base border"
                  >
                    <div>
                      <h4 className="mb-1 text-body-emphasis fw-bold">{item.name}</h4>
                      <p className="mb-0 text-body-tertiary small lh-1">{item.description}</p>
                    </div>
                    <FeatherIcon icon="chevron-right" size={16} className="text-primary" />
                  </Link>
                ))}
              </div>
            </Col>

            {/* Column 2: Features (Middle) */}
            <Col className="mega-menu-col col-4 p-4 border-end">
              <div className="d-flex flex-column gap-2">
                {featuresSection.items.map((item, index) => (
                  <div
                    key={index}
                    onMouseEnter={() => setActiveFeatureIndex(index)}
                    className={classNames(
                      'd-flex align-items-center gap-3 p-3 rounded-3 cursor-pointer transition-base',
                      {
                        'bg-body-highlight': activeFeatureIndex === index,
                        'hover-bg-body-highlight': activeFeatureIndex !== index
                      }
                    )}
                  >
                    {item.icon && (
                      <div className="p-2 bg-body rounded-2 shadow-sm border">
                        <FeatherIcon
                          icon={typeof item.icon === 'string' ? item.icon : 'circle'}
                          size={20}
                          className="text-primary"
                        />
                      </div>
                    )}
                    <span
                      className={classNames('fw-semibold', {
                        'text-primary': activeFeatureIndex === index,
                        'text-body': activeFeatureIndex !== index
                      })}
                    >
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </Col>

            {/* Column 3: Details (Right) */}
            <Col className="mega-menu-col col-5 p-5 d-flex flex-column justify-content-center">
              {activeFeature && (
                <div className="fade-in">
                  <h3 className="fw-bold mb-3 ls-tight">{activeFeature.detailTitle || activeFeature.name}</h3>
                  <p className="text-body-secondary fs-8 mb-5">{activeFeature.detailDescription}</p>

                  <Link
                    to={activeFeature.path || '#'}
                    className="d-inline-flex align-items-center justify-content-center p-3 rounded-circle bg-primary-subtle text-primary transition-base hover-bg-primary hover-text-white"
                  >
                    <FeatherIcon icon="arrow-up-right" size={24} />
                  </Link>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Dropdown.Menu>
    );
  }

  return (
    <Dropdown.Menu
      as="ul"
      className="dropdown-menu-card dropdown-mega-menu py-0 border-0 shadow-sm w-100 rounded-0"
      style={{ marginTop: '0.1rem' }}
    >
      <div className="mega-menu-panel px-4 pb-4 pt-5 rounded-0">
        <Row className="gx-4 gy-4 mega-menu-row flex-nowrap">
          {sections.map((section, sectionIndex) => (
            <Col key={sectionIndex} className={classNames('mega-menu-col', section.containerClass)}>
              <h4 className={classNames('mega-menu-section-title text-uppercase fw-bold mb-3', section.titleClass)}>
                {section.sectionTitle}
              </h4>
              <div className="mega-menu-section-items">
                {section.items.map((item, itemIndex) => (
                  <MegaMenuItem key={itemIndex} item={item} />
                ))}
                {section.allLink && (
                  <Link
                    to={section.allLink.path}
                    className="mega-menu-all-link d-inline-block mt-2 text-primary fw-semibold text-decoration-none"
                  >
                    {section.allLink.label} →
                  </Link>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </div>
      {route.megaMenuBottomBar && (
        <div className="mega-menu-bottom-bar bg-body-tertiary p-3 rounded-bottom-3 border-top">
          <div className="d-flex align-items-center justify-content-center gap-2">
            {route.megaMenuBottomBar.icon && (
              <FeatherIcon icon={route.megaMenuBottomBar.icon} size={16} className="text-primary" />
            )}
            <span className="fw-semibold text-body-secondary">{route.megaMenuBottomBar.label}</span>
            <Link to={route.megaMenuBottomBar.linkPath} className="fw-bold text-decoration-none">
              {route.megaMenuBottomBar.linkText}
            </Link>
          </div>
        </div>
      )}
    </Dropdown.Menu>
  );
};

function buildSectionsFromPages(pages: Route[] = []): MegaMenuSection[] {
  if (!pages.length) return [];
  const cols: Route[][] = [[], [], []];
  pages.forEach((page, index) => {
    cols[index % 3].push(page);
  });
  const sectionTitles = ['Features', 'Premium', 'Solutions'];
  return cols
    .filter(col => col.length > 0)
    .map((items, i) => ({
      sectionTitle: sectionTitles[i] ?? `Section ${i + 1}`,
      items
    }));
}

interface MegaMenuItemProps {
  item: Route;
  onCloseMenu?: () => void;
}

const MegaMenuItem = ({ item, onCloseMenu }: MegaMenuItemProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const iconName = typeof item.icon === 'string' ? item.icon : 'circle';
  const isActive = item.path && pathname === item.path;
  const path = item.path && item.path !== '#!' && item.path !== '#' ? item.path : undefined;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (path) {
      onCloseMenu?.();
      navigate(path);
    }
  };

  const className = classNames('mega-menu-item d-flex align-items-start gap-2 pt-4 rounded text-decoration-none mb-2', {
    active: isActive
  });

  const content = (
    <>
      <span className="mega-menu-item-icon flex-shrink-0 d-flex align-items-center">
        <FeatherIcon icon={iconName} size={22} className="text-body" />
      </span>
      <div className="mega-menu-item-content min-w-0 flex-grow-1">
        <span className="fw-bold d-block mega-menu-item-title">{capitalize(item.name)}</span>
        {item.description && (
          <span className="text-body-secondary d-block mt-1 mega-menu-item-description">{item.description}</span>
        )}
      </div>
    </>
  );

  if (!path) {
    return (
      <span className={className} style={{ cursor: 'default' }}>
        {content}
      </span>
    );
  }

  return (
    <a href={path} onClick={handleClick} className={className} style={{ cursor: 'pointer', color: 'inherit' }}>
      {content}
    </a>
  );
};

export default TopNavMegaMenu;
